/**
 * CRM API client — thin wrappers over the shared dashboard apiFetch helpers
 * (Bearer auth, /api base). Every screen reads through these; the useCrm hook
 * adds graceful degradation so the UI keeps working on mock data when the
 * backend isn't live yet (404/401/405/timeout → fall back).
 */
import { apiGet, apiPost, apiPut, apiDelete, apiFetch } from '../../utils/api'

const patch = async <T = any>(endpoint: string, body?: any): Promise<T> => {
  const res = await apiFetch(endpoint, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined })
  return res.json()
}

// invoices — file bytes go through the platform's existing agent upload; then we
// record the invoice link against the deal.
export const uploadFile = async (file: File): Promise<{ id: string; url: string; type: string; name: string; size: number }> => {
  const fd = new FormData()
  fd.append('file', file)
  const token = localStorage.getItem('auth_token')
  const res = await fetch('/api/agent/upload', { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd })
  if (!res.ok) { const e: Error & { status?: number } = new Error('Upload failed'); e.status = res.status; throw e }
  return res.json()
}
// module setup / live status (Phase 6)
export const getModuleStatuses = (dealId: string) => apiGet<{ modules: any[] }>(`/crm/deals/${dealId}/modules`).then((r) => r.modules)
export const setModuleStatus = (dealId: string, key: string, body: { setupState?: string; live?: boolean }) => apiPut(`/crm/deals/${dealId}/modules/${key}`, body)

// Platform usage — activity COUNTS per pushed period (+ engagement + presence).
// 404s until WeQuote pushes the first period; useCrm then keeps the sample.
export const getPlatformUsage = (dealId: string) => apiGet(`/crm/deals/${dealId}/usage`)
// Shows & Events — real outcomes from the LINKED DEAL's stage (Phase 2.4), not from
// opp.status (which the lead import never sets, so the old rate read 0% for every show).
export interface EventRoi {
  groupId: string; name: string; type: string; date: string | null; cost: number | null
  captured: number; inCrm: number; notFollowedUp: number
  demos: number; trials: number; customers: number; lost: number; churned: number; open: number
  captureToCustomerPct: number; captureToTrialPct: number; crmToCustomerPct: number
  mrr: number; pipeline: number; roi: number | null; costPerLead: number | null; costPerCustomer: number | null
  medianDaysToTrial: number | null; medianDaysToCustomer: number | null
}
export const getEventRoi = () => apiGet<{ events: EventRoi[] }>('/crm/events/roi').then((r) => r.events)
export const updateOppGroup = (id: string, body: { cost?: number | null; name?: string; type?: string; date?: string; endDate?: string | null; budget?: number | null; location?: string; notes?: string }) => apiPut(`/crm/groups/${id}`, body)

// ── Event Planner (7 Sep 2026) — same events as Shows/Events, plus the money side. ──
export interface PlannerEvent extends EventRoi {
  endDate: string | null; budget: number | null; location: string | null
  spent: number; expenseCount: number
  /** Companies, deduped — several people from one company = one lead. */
  uniqueLeads: number
  /** Customers the event actually won (went live after its start date). */
  newCustomers: number
  /** Open pipeline only — Won is its own number, never merged in. */
  estPipelineAnnual: number; actualAnnualValue: number; net: number
  /** Monthly-accruing money from customers who signed up AFTER the event. */
  earned: number; leftToBreakeven: number; profitOverBreakeven: number
}
export interface ExpenseLink { id: string; kind: 'link' | 'file' | 'image'; url: string; name: string | null; mime?: string | null; size?: number | null }
export interface EventExpense {
  id: string; groupId: string; label: string; amount: number; category: string | null
  incurredAt: string | null; createdBy: string | null; createdAt: string; links: ExpenseLink[]
}
export interface EventLeadValue {
  oppId: string; dealId: string | null; avatarUrl: string | null; name: string; company: string | null; stage: string | null
  sizeBand: string | null; assumed: boolean; estAnnual: number; isCustomer: boolean; actualAnnual: number | null
  /** Already live before the event started — £0 to this event. */
  preExisting: boolean
}
export const getPlannerEvents = () => apiGet<{ events: PlannerEvent[]; anchors: Record<string, number>; anchorBasis: Record<string, number> }>('/crm/planner/events')
export const getEventLeadValues = (groupId: string) => apiGet<{ leads: EventLeadValue[] }>(`/crm/planner/events/${groupId}/leads`).then((r) => r.leads)
export const listEventExpenses = (groupId: string) => apiGet<{ expenses: EventExpense[] }>(`/crm/planner/events/${groupId}/expenses`).then((r) => r.expenses)
export const addEventExpense = (groupId: string, body: { label: string; amount: number; category?: string | null; incurredAt?: string | null }) => apiPost<EventExpense>(`/crm/planner/events/${groupId}/expenses`, body)
export const updateEventExpense = (id: string, body: { label?: string; amount?: number; category?: string | null; incurredAt?: string | null }) => apiPut<EventExpense>(`/crm/planner/expenses/${id}`, body)
export const deleteEventExpense = (id: string) => apiDelete(`/crm/planner/expenses/${id}`)
export const addExpenseUrl = (expenseId: string, body: { url: string; name?: string | null }) => apiPost<ExpenseLink>(`/crm/planner/expenses/${expenseId}/links`, body)
export const deleteExpenseLink = (id: string) => apiDelete(`/crm/planner/links/${id}`)
// ── touchpoints (17 Sep 2026): 5 ways to reach a lead + Brevo truth ──
export interface BrevoStatus {
  configured: boolean; checked?: string; found?: boolean; blacklisted?: boolean
  lists?: string[]; lastEmailAt?: string | null; sends90d?: number; error?: string
}
export interface Touchpoints {
  contactId: string | null; contactName: string | null; availableCount: number
  email: { available: boolean; value: string | null; brevo: BrevoStatus }
  telephone: { available: boolean; value: string | null }
  mobile: { available: boolean; value: string | null }
  linkedin: { available: boolean; value: string | null }
  whatsapp: { available: boolean; value: string | null; assumedFromPhone: boolean }
}
/** Per-lead touchpoint health for list rows. Ranked LinkedIn>Mobile>Email>Mailing list>Telephone; ml is tri-state (null = not checked yet). */
export interface TouchSummary { li: boolean; mo: boolean; em: boolean; ml: boolean | null; te: boolean; count: number; score: number }
export const getTouchpointsSummary = () => apiGet<{ byDeal: Record<string, TouchSummary> }>('/crm/touchpoints/summary').then((r) => r.byDeal)
export const getTouchpoints = (dealId: string) => apiGet<Touchpoints>(`/crm/deals/${dealId}/touchpoints`)
export const patchTouchpoints = (dealId: string, body: { email?: string | null; telephone?: string | null; mobile?: string | null; linkedin?: string | null; whatsapp?: string | null }) => patch<Touchpoints>(`/crm/deals/${dealId}/touchpoints`, body)

// ── lead images / uploaded avatars ──
export interface DealImage { id: string; dealId: string; url: string; name: string | null; mime: string | null; size: number | null; isAvatar: boolean; createdBy: string | null; createdAt: string }
export const getDealImages = (dealId: string) => apiGet<{ images: DealImage[] }>(`/crm/deals/${dealId}/images`).then((r) => r.images)
export const setImageAsAvatar = (imageId: string) => apiPost<DealImage>(`/crm/images/${imageId}/avatar`, {})
export const deleteDealImage = (imageId: string) => apiDelete(`/crm/images/${imageId}`)
export const uploadDealImage = async (dealId: string, file: File): Promise<DealImage> => {
  const fd = new FormData()
  fd.append('file', file)
  const token = localStorage.getItem('auth_token')
  const res = await fetch(`/api/crm/deals/${dealId}/images`, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd })
  if (!res.ok) { const e: Error & { status?: number } = new Error('Upload failed'); e.status = res.status; throw e }
  return res.json()
}

export const uploadExpenseFile = async (expenseId: string, file: File): Promise<ExpenseLink> => {
  const fd = new FormData()
  fd.append('file', file)
  const token = localStorage.getItem('auth_token')
  const res = await fetch(`/api/crm/planner/expenses/${expenseId}/files`, { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd })
  if (!res.ok) { const e: Error & { status?: number } = new Error('Upload failed'); e.status = res.status; throw e }
  return res.json()
}

// Module adoption derived from usage (Phase 2.3) — not from the Setup checkbox.
export interface AdoptionModule { key: string; label: string; state: 'in_use' | 'set_up_unused' | 'dormant' | 'never' | 'unknown'; current: number; prior: number; trendPct: number | null; setupState: string; markedLive: boolean; mismatch: 'live_but_unused' | 'used_but_not_marked' | null }
export interface DealAdoption { dealId: string; hasUsage: boolean; seeded: boolean; modules: AdoptionModule[]; inUse: number; setUpUnused: number; dormant: number; effectiveLive: number }
export const getAdoptionSummary = () => apiGet<{ deals: Record<string, DealAdoption> }>('/crm/adoption/summary').then((r) => r.deals)
export const getDealAdoption = (dealId: string) => apiGet<DealAdoption>(`/crm/deals/${dealId}/adoption`)
export const getStalledRollouts = () => apiGet<{ rollouts: { dealId: string; company: string; stalled: string[]; seeded: boolean }[] }>('/crm/adoption/stalled').then((r) => r.rollouts)

// Churn / health scored from evidence (Phase 2.2). `coverage` says which of the five
// sources actually spoke — a score with no coverage is 'unknown', never 'healthy'.
export type HealthSource = 'payments' | 'usage' | 'engagement' | 'support' | 'adoption'
export interface ServerHealth {
  dealId: string
  score: number
  level: 'high' | 'med' | 'low' | 'churned' | 'unknown'
  reasons: { source: HealthSource | 'lifecycle'; delta: number; label: string }[]
  coverage: HealthSource[]
  coverageCount: number
  seeded: boolean
  evidence: Record<string, number | null | undefined>
}
export const getHealthSummary = () => apiGet<{ deals: Record<string, ServerHealth> }>('/crm/health/summary').then((r) => r.deals)
export const getDealHealth = (dealId: string) => apiGet<ServerHealth>(`/crm/deals/${dealId}/health`)

// WeQuote live-customer sync — manual, on a button. Preview (dryRun) writes nothing.
export interface SyncRow { action: 'create' | 'update' | 'unchanged' | 'skip'; accountId: string; company: string; dealId?: string; changes?: string[]; reason?: string }
export interface SyncResult { dryRun: boolean; source: 'api' | 'inline'; total: number; created: number; updated: number; unchanged: number; skipped: number; archived?: number; coverage?: { activatedSent: number; activatedParseable: number; trialStartSent: number; trialEndSent: number }; rows: SyncRow[]; ranAt: string }
export interface SyncStatus { configured: boolean; hasKey: boolean; lastSync: { ranAt: string; total: number; created: number; updated: number; unchanged: number; skipped: number; archived?: number } | null; needs: string[] }
export const getCustomerSyncStatus = () => apiGet<SyncStatus>('/crm/sync/wequote-customers')
export const runCustomerSync = (body: { dryRun?: boolean; customers?: unknown[] }) => apiPost<SyncResult>('/crm/sync/wequote-customers', body)
// Background sync job (the Apply path) — start it, then poll progress for the bar.
export interface SyncCoverage { activatedSent: number; activatedParseable: number; trialStartSent: number; trialEndSent: number }
export interface SyncJobProgress {
  running: boolean
  dryRun?: boolean
  phase: 'idle' | 'pulling' | 'syncing' | 'done' | 'error'
  processed?: number
  total?: number
  startedAt?: string
  finishedAt?: string
  error?: string
  result?: SyncResult & { coverage?: SyncCoverage }
}
export const startCustomerSyncJob = () => apiPost<{ started: boolean; job?: SyncJobProgress }>('/crm/sync/wequote-customers/start', {})
export const getCustomerSyncProgress = () => apiGet<SyncJobProgress>('/crm/sync/wequote-customers/progress')

// Support ↔ CRM join (Phase 2.1) — tickets + chats + support account for a lead,
// matched on contact email / company alias / wequote_account_id. Read live.
export const getDealSupport = (dealId: string) => apiGet(`/crm/deals/${dealId}/support`)
// Board-level: open tickets + last support contact, keyed by deal id.
export const getSupportSummary = () =>
  apiGet<{ deals: Record<string, { openTickets: number; ticketsTotal: number; lastTicketDays: number | null; chatsTotal: number; lastChatDays: number | null; lastContactDays: number | null }> }>('/crm/support/summary').then((r) => r.deals)

// Board-level: latest period total + active days + last seen, keyed by deal id.
export const getUsageSummary = () =>
  apiGet<{ deals: Record<string, { total: number | null; activeDays: number | null; periodDays: number; seeded?: boolean; lastSeenAt?: string | null; lastSeenDays?: number | null; activeUsers?: number }> }>('/crm/usage/summary').then((r) => r.deals)
export const getInvoices = (dealId: string) => apiGet<{ invoices: any[] }>(`/crm/deals/${dealId}/invoices`).then((r) => r.invoices)
export const addInvoice = (dealId: string, body: { fileUrl: string; filename: string; size?: number; mimetype?: string }) => apiPost(`/crm/deals/${dealId}/invoices`, body)
export const deleteInvoice = (id: string) => apiDelete(`/crm/invoices/${id}`)

// lead-import mapping tool (26-col unified schema)
export interface LeadParse {
  filename: string
  sheets: string[]
  sheet: string
  headerRowNumber: number
  headers: string[]
  rows: string[][]
  rowCount: number
  samples: Record<string, string>
}
export const getLeadImportSchema = () => apiGet<{ groups: { name: string; color: string; fields: string[] }[]; required: string[] }>('/crm/leads/import/schema')
export const getImportExistingEmails = () => apiGet<{ emails: string[] }>('/crm/leads/import/existing-emails').then((r) => r.emails)
export interface LeadImportResult { created: number; updated: number; skipped: number; flagged?: number; total: number; opportunity: string; opportunities?: string[]; groupId?: string }
export const commitLeadImport = (body: { opportunity: string; owner?: string; capturedDate?: string; leadSource?: string; records: Record<string, string>[] }) =>
  apiPost<LeadImportResult>('/crm/leads/import/commit', body)
export const parseLeadImport = async (file: File, sheet?: string): Promise<LeadParse> => {
  const fd = new FormData()
  fd.append('file', file)
  if (sheet) fd.append('sheet', sheet)
  const token = localStorage.getItem('auth_token')
  const res = await fetch('/api/crm/leads/import/parse', { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd })
  if (!res.ok) {
    let d: any = null
    try { d = await res.json() } catch { /* ignore */ }
    const e: Error & { status?: number } = new Error(d?.error?.message || d?.message || 'Could not read the file')
    e.status = res.status
    throw e
  }
  return res.json()
}

// Meetings (Calendly demos) — history + upcoming for a lead.
export interface DealDemo {
  id: string
  eventName: string
  startAt: string | null
  endAt: string | null
  upcoming: boolean
  status: 'booked' | 'cancelled' | string
  inviteeName: string | null
  inviteeEmail: string | null
  hostName: string | null
  hostEmail: string | null
  meetUrl: string | null
  transcriptUrl: string | null
  notesText: string | null
  notesStatus: string | null
  matchedBy: string | null
  /** Held vs no-show — auto from Meet join records, manually editable. */
  attendanceStatus?: 'held' | 'no_show' | null
  attendanceSource?: 'auto' | 'manual' | null
  questions: { question: string; answer: string }[]
}
export const getDealDemos = (dealId: string) => apiGet<{ demos: DealDemo[] }>(`/crm/deals/${dealId}/demos`).then((r) => r.demos)
/** Manually set/clear Held vs No-Show on a Calendly demo. */
export const setDemoAttendance = (id: string, status: 'held' | 'no_show' | null) => {
  return apiFetch(`/crm/demos/${id}/attendance`, { method: 'PATCH', body: JSON.stringify({ status }) }).then((r) => r.json())
}
export const getAllDemos = () => apiGet<{ demos: (DealDemo & { dealId: string | null })[] }>('/crm/demos').then((r) => r.demos)
export const setDemoTranscript = (demoId: string, url: string | null) => apiPut(`/crm/demos/${demoId}/transcript`, { url })

// meta / stages / board
export const getMeta = () => apiGet('/crm/meta')
export const getStages = () => apiGet<{ stages: any[] }>('/crm/stages').then((r) => r.stages)
export const getBoard = () => apiGet('/crm/board')

/* ─── Onboardings (Lee, 28 Sep 2026) ─────────────────────────────────────── */
/* v2 (30 Sep 2026): nine stages; steps are rich objects. */
export interface ObStepMeta {
  t: string; tag: 'AUTO' | 'MANUAL' | 'CALL' | 'BOOK'; res?: string[]; we?: string
  optional?: boolean; note?: string; subs?: string[]; subLabel?: string
  counter?: number; fact?: string; subBp?: number; tn?: boolean
  phs?: Array<{ lead: string; text: string; internal: boolean }>; milestone?: string; blk?: number
}
export interface ObStageMeta {
  name: string; short: string; path: string; intro?: string
  unlock?: 'accepted' | 'three'; module?: 'wh' | 'pr' | 'bp'; proposed?: boolean
  side?: { items: string[]; res?: string[] } | null
  blocks: Array<{ title?: string; note?: string; covered?: string[] }>
  steps: ObStepMeta[]
}
export interface ObPlanMeta { hrs: number; gbp: number; usd: number; poa?: boolean; blurb: string; items: string[] }
export interface ObConfig {
  stages: ObStageMeta[]
  plans: Record<string, ObPlanMeta>
  modulesNote: string
  columns: Array<{ key: string; label: string; color: string }>
  riskStatuses: string[]
  riskTriggers: Array<{ key: string; hint: string }>
  autoStepsEnabled: boolean
}
export interface Onboarding {
  id: string
  leadId: string | null
  /** Customer's pending hours request (clears when hours are topped up). */
  hoursRequest?: { hours: number | null; note: string | null; at: string } | null
  companyName: string
  slug: string
  plan: string | null
  /** 'requested' = ONBOARDING REQUIRED — the company went paid (server sweep, Lee 12 Oct 2026); we assign the plan. */
  planState: 'awaiting' | 'requested' | 'gold_requested' | 'active'
  requestedAt?: string | null
  /** Employees — from the linked lead's qualification company size. */
  companySize?: number | null
  /** Lead's country (flag on the board card). */
  country?: string | null
  /** Latest synced email with the company — who → who, when, via which mailbox. */
  lastEmail?: { at: string | null; direction: 'in' | 'out'; from: string; to: string | null; via: string } | null
  /** Latest held meeting (lead bookings incl. the calendar mirror). */
  lastMeeting?: { at: string | null; title: string | null; type: string | null; assigneeId: string | null; by: string | null } | null
  /** Latest HELD onboarding meeting/session — primary board sort key. */
  lastHeldAt?: string | null
  hoursAllowance: number | null
  price: number | null
  currency: string | null
  /** 'processing' = delayed bank method initiated via Stripe; 'failed' = async payment failed (plan stays active, blaring banner). */
  paymentStatus: 'pending' | 'processing' | 'paid' | 'failed' | null
  paymentLinkUrl: string | null
  ownerId: string | null
  goLiveDate: string | null
  column: 'notstarted' | 'progress' | 'complete' | 'archived'
  archivedAt?: string | null
  override: { column: string; reason: string; by: string; at: string } | null
  risk: { status: string; reasons: string[]; other: string | null; ownerId: string | null; notes: string | null } | null
  suggestionDismissedAt: string | null
  discoveryCallAt: string | null
  lastLoginAt: string | null
  lastQuoteAt: string | null
  lastQuoteAcceptedAt: string | null
  suggestions: string[]
  hoursUsed: number
  hoursOver?: number
  overNotice?: { at: string; by: string; over: number } | null
  stages: Array<{ done: number; total: number; name?: string; short?: string; allDone?: number; allTotal?: number; locked?: boolean; hidden?: boolean; addon?: boolean; lockMsg?: string; counted?: boolean; complete?: boolean; proposed?: boolean; hours?: number }>
  mods?: { wh: boolean; pr: boolean; bp?: boolean }
  biz?: { inv?: string | null; sales?: number | null; techs?: number | null }
  custom?: Array<Array<{ t: string; done: boolean; by?: string | null; requiresUpload?: boolean; url?: string | null; urlKind?: 'page' | 'tutorial' }>>
  facts?: { wh: boolean; pr: boolean; bp: boolean; three: boolean; accepted: boolean }
  segments?: Array<{ short: string; done: number; total: number; locked: boolean }>
  currentStage: number
  stepsDone: number
  stepsTotal: number
  nextMeeting: { startAt: string; subject: string | null } | null
  stagingMeta: Record<string, any> | null
  steps?: Array<{ stageIndex: number; stepIndex: number; mode: string; done: boolean; doneBy: string | null; doneAt: string | null; subs: boolean[]; n: number; link?: { kind: 'page' | 'tutorial' | 'book'; url?: string } | null; tutorialUrl?: string | null }>
  meetings?: Array<{ id: string; stageIndex: number | null; startAt: string | null; durationMin: number | null; subject: string | null; attendees: string[]; status: string; summary: string | null; hasTranscript: boolean; transcriptRef?: string | null; bookedBy: string | null; covered?: boolean[]; blockers?: string | null; nextAct?: string | null; nextOwnerId?: string | null; nextDue?: string | null }>
}
export const getObConfig = () => apiGet<ObConfig>('/crm/onboardings/config')
export const getOnboardings = () => apiGet<{ onboardings: Onboarding[]; feedSyncedAt?: string | null }>('/crm/onboardings')
export const getOnboarding = (id: string) => apiGet<{ onboarding: Onboarding }>(`/crm/onboardings/${id}`).then((r) => r.onboarding)
export const obSyncStaging = () => apiPost<{ pulled: number; created: number; existing: number }>('/crm/onboardings/sync-staging')
export const obSetOverride = (id: string, column: string | null, reason?: string) => patch(`/crm/onboardings/${id}/override`, { column, reason: reason ?? null })
export const obArchive = (id: string, archived: boolean) => apiPost(`/crm/onboardings/${id}/archive`, { archived })
export const obSetStep = (id: string, stageIndex: number, stepIndex: number, done: boolean) => patch<{ movedTo: string | null }>(`/crm/onboardings/${id}/step`, { stageIndex, stepIndex, done })
export const obSetPlan = (id: string, body: { plan: string; price?: number | null; currency?: string | null; paymentLinkUrl?: string | null; hours?: number | null }) => patch(`/crm/onboardings/${id}/plan`, body)
export const obAddHours = (id: string, hours: number, reason?: string) => apiPost<{ hoursAllowance: number }>(`/crm/onboardings/${id}/hours`, { hours, reason: reason ?? null })
export const obSendOverNotice = (id: string) => apiPost<{ ok: boolean; notice: { at: string; over: number } }>(`/crm/onboardings/${id}/over-notice`, {})
export const obMarkPaid = (id: string) => apiPost(`/crm/onboardings/${id}/mark-paid`)
export const obRotateToken = (id: string) => apiPost<{ token: string }>(`/crm/onboardings/${id}/rotate-token`)
export const obUpdate = (id: string, body: { ownerId?: string | null; goLiveDate?: string | null; manualHours?: number; companyName?: string }) => patch(`/crm/onboardings/${id}`, body)
export const obByLead = (leadId: string) => apiGet<{ onboarding: Onboarding }>(`/crm/onboardings/by-lead/${leadId}`).then((r) => r.onboarding)
export const obStart = (companyName: string, leadId: string) => apiPost<{ onboarding: any; token?: string }>('/crm/onboardings', { companyName, leadId })
export const obGetLink = (id: string) => apiGet<{ url: string; slug: string }>(`/crm/onboardings/${id}/link`)
export const obCreateMeeting = (id: string, body: { stageIndex?: number | null; startAt: string; durationMin?: number | null; subject?: string | null; attendees?: string[] }) => apiPost<{ id: string }>(`/crm/onboardings/${id}/meetings`, body)
export const obUpdateMeeting = (id: string, mid: string, body: { status?: string; summary?: string | null; startAt?: string; durationMin?: number | null; subject?: string | null; stageIndex?: number | null }) => patch(`/crm/onboardings/${id}/meetings/${mid}`, body)
export const obGoldPrice = (id: string, price: number, currency?: 'GBP' | 'USD', paymentLinkUrl?: string | null) => apiPost(`/crm/onboardings/${id}/request-gold-price`, { price, currency, paymentLinkUrl: paymentLinkUrl || null })
export const obSetRisk = (id: string, body: { status: string | null; reasons?: string[]; other?: string | null; ownerId?: string | null; notes?: string | null }) => patch(`/crm/onboardings/${id}/risk`, body)
export const obDismissSuggestion = (id: string) => apiPost(`/crm/onboardings/${id}/risk/dismiss-suggestion`)
export const obReset = (id: string) => apiPost(`/crm/onboardings/${id}/reset`)
export const obSetSub = (id: string, stageIndex: number, stepIndex: number, subIndex: number, ticked: boolean) => patch(`/crm/onboardings/${id}/sub`, { stageIndex, stepIndex, subIndex, ticked })
export const obSetCounter = (id: string, stageIndex: number, stepIndex: number, n: number) => patch(`/crm/onboardings/${id}/counter`, { stageIndex, stepIndex, n })
export const obSetAbout = (id: string, body: { mods?: { wh: boolean; pr: boolean; bp?: boolean }; biz?: { inv?: string | null; sales?: number | null; techs?: number | null } }) => patch(`/crm/onboardings/${id}/about`, body)
export const obCustomTask = (id: string, body: { stageIndex: number; action: 'add' | 'toggle' | 'remove'; text?: string; index?: number; requiresUpload?: boolean; url?: string; urlKind?: 'page' | 'tutorial' }) => apiPost(`/crm/onboardings/${id}/custom`, body)
/** Sessions timeline (Lee, 12 Oct 2026). */
export interface ObSessionTask { stageIndex: number; index: number; text: string; done: boolean; src: string | null; by: string | null }
export interface ObSession { id: string; startAt: string | null; durationMin: number | null; subject: string | null; status: string; stageIndex: number | null; attendees: string[]; transcriptRef: string | null; summary: string | null; blockers: string | null; nextAct: string | null; nextOwnerId: string | null; nextDue: string | null; covered: boolean[]; bookedBy: string | null; eventId: string | null; notes: Array<{ owner: string; docUrl: string | null; notesText: string; pulledAt: string | null }>; tasks: ObSessionTask[] }
export const obSessions = (id: string) => apiGet<{ sessions: ObSession[]; otherPending: ObSessionTask[] }>(`/crm/onboardings/${id}/sessions`)
export const obMeetingNotes = (id: string, mid: string, body: { covered?: boolean[]; blockers?: string | null; nextAct?: string | null; nextOwnerId?: string | null; nextDue?: string | null }) => patch(`/crm/onboardings/${id}/meetings/${mid}/notes`, body)
export const obFiles = (id: string) => apiGet<{ files: any[] }>(`/crm/onboardings/${id}/files`).then((r) => r.files)
export const obUploadFile = (id: string, body: { stageIndex?: number | null; stepIndex?: number | null; customIndex?: number | null; name: string; dataBase64: string; mime?: string }) => apiPost(`/crm/onboardings/${id}/files`, body)
export const obAttachLink = (id: string, body: { stageIndex?: number | null; stepIndex?: number | null; customIndex?: number | null; name: string; url: string }) => apiPost(`/crm/onboardings/${id}/files`, body)
export const obSetStepTutorial = (id: string, body: { stageIndex: number; stepIndex: number; url: string | null }) => patch<{ ok: boolean; tutorialUrl: string | null }>(`/crm/onboardings/${id}/step-tutorial`, body)
export const obResources = () => apiGet<{ resources: Record<string, { label: string | null; url: string | null }> }>(`/crm/onboardings-resources`).then((r) => r.resources)
export const obSetResource = (key: string, body: { label?: string | null; url?: string | null }) => apiFetch(`/crm/onboardings-resources/${key}`, { method: 'PUT', body: JSON.stringify(body) }).then((r) => r.json())
/* Distinct lead timezones + counts — the wall-clock strip (server caches 2h). */
export interface LeadTimezone { tz: string; count: number }
export const getLeadTimezones = () => apiGet<{ zones: LeadTimezone[] }>('/crm/timezones').then((r) => r.zones)
export const getUsers = () => apiGet<{ users: any[] }>('/crm/users').then((r) => r.users)

// deals
export const getDeals = (params?: Record<string, any>) => apiGet<{ deals: any[]; total: number }>('/crm/deals', params)
export interface AcCustomer {
  id: string; company: string; plan: string; mrr: number
  paidAt: string; paidDays: number
  billing: { label: string; frequency: string; termMonths: number | null; source: string }
  daysSinceQuote: number | null; lastQuoteAt: string | null; quotes14: number
  daysSinceTouch?: number | null; lastTouchAt?: string | null
  noData: boolean; feedStale: boolean; feedStaleSince: string | null; exactQuoteData: boolean; lastActiveAt: string | null
  owned: boolean[]; paidN: number
  left: Array<{ key: string; label: string; monthly: number; perUser: boolean }>
  potential: number; gap: number; captured: number; status: string
  trend: Array<{ start: string; end: string; quotes: number; logins: number }>
}
export interface AcPayload {
  kpis: { paidCustomers: number; mrr: number; potential: number; untapped: number; mrrAtRisk: number; atRiskCount: number; capturedPct: number }
  customers: AcCustomer[]
  generatedAt: string
}
export const getAntiChurn = () => apiGet<AcPayload>('/crm/anti-churn')
export interface MyTodo { id: string; text: string; done: boolean; doneAt: string | null; createdAt: string }
export const getMyTodos = () => apiGet<{ todos: MyTodo[] }>('/crm/my-todos').then((r) => r.todos)
export const addMyTodo = (text: string) => apiPost<{ id: string }>('/crm/my-todos', { text })
export const patchMyTodo = (id: string, body: { done?: boolean; text?: string }) => patch(`/crm/my-todos/${id}`, body)
export const deleteMyTodo = (id: string) => apiDelete(`/crm/my-todos/${id}`)
export interface DealCall { id: string; direction: 'out' | 'in'; outcome: string; durationMin: number | null; notes: string | null; at: string | null; loggedBy: string | null; source: string }
export const listDealCalls = (dealId: string) => apiGet<{ calls: DealCall[] }>(`/crm/deals/${dealId}/calls`).then((r) => r.calls)
export const logDealCall = (dealId: string, body: { direction?: 'out' | 'in'; outcome: string; durationMin?: number | null; notes?: string | null; at?: string | null }) => apiPost<{ id: string }>(`/crm/deals/${dealId}/calls`, body)
export const deleteDealCall = (dealId: string, callId: string) => apiDelete(`/crm/deals/${dealId}/calls/${callId}`)

/* ─── Contract Manager (Lee, 6 Oct 2026) ─────────────────────────────────── */
export interface CtPriceBook {
  currency: string; vatNote: string
  packages: Record<string, { label: string; monthly: number; blurb?: string; tbc?: boolean }>
  snagg: { label: string; perUserMonthly: number; min: number; max: number }
  modules: Record<string, { label: string; monthly: number }>
  integrations: Record<string, { label: string; monthly: number }>
  discounts: Record<string, { monthly: number; annual: number }>
  proserv: { migrationDefault: number; training: Record<string, { label: string; fee: number; tbc?: boolean; details?: string[] }>; extraDay: number; extraHour: number; scannerEach?: number }
}
export interface CtSelections { package: string; termMonths: 12 | 24 | 36; frequency: 'monthly' | 'annual'; snaggUsers: number; modules: string[]; integrations: string[]; training: string; includedKeys?: string[] }
export interface CtOptions { choosePackage: boolean; chooseTerm: boolean; chooseFrequency: boolean; chooseSnagg: boolean; chooseModules: boolean; chooseIntegrations: boolean; chooseTraining?: boolean; shownPackages?: string[] | null; hideSow?: boolean }
export interface Contract {
  id: string; leadId: string; slug: string; status: 'draft' | 'sent' | 'signed' | 'countersigned' | 'void'
  customer: Record<string, any>; pricing: CtPriceBook; selections: CtSelections; options: CtOptions
  proserv: Record<string, any>; sowFills: Record<string, string>; sowComplete: boolean; sign: any; countersign: any
  sentAt: string | null; viewedAt: string | null; signedAt: string | null
  startAt: string | null
  computed: any; locked: boolean; createdAt: string | null; updatedAt: string | null
}
export interface CtSummary {
  id: string; leadId: string; slug: string; status: string; company: string
  plan: string; termMonths: number; frequency: 'monthly' | 'annual'
  mrr: number; billedAnnually: number; currency: string
  hasSnagg: boolean; hasProcurement: boolean; hasIntegrations: boolean
  renewedFrom: string | null; startAt: string | null; commencementAt: string | null; termEndAt: string | null
  sentAt: string | null; signedAt: string | null; updatedAt: string; createdAt: string
}
export interface CtMonitorCard {
  contractId: string; leadId: string; company: string; plan: string
  frequency: 'monthly' | 'annual'; currency: string; amount: number
  dueAt?: string; status?: 'expected' | 'paid' | 'failed'
  renewalAt?: string; overdue?: boolean; termMonths?: number
  managedBy?: 'wequote'
}
export interface CtLegacy {
  id: string; leadId: string | null; company: string; dealName: string | null
  tncs: 'old' | 'new'
  plan: 'lite' | 'pro' | 'premium' | 'enterprise'; mrr: number | null
  durationMonths: number | null; frequency: 'monthly' | 'annual' | null
  commencedAt: string | null; source: string
  nextRenewalAt: string | null; tncsLabel: string | null; notes: string | null
  hasSnagg: boolean; hasProcurement: boolean; hasIntegrations: boolean
  migratedContractId: string | null; migratedAt: string | null
}
export const ctList = () => apiGet<{ contracts: CtSummary[] }>('/crm/contracts').then((r) => r.contracts)
export const ctByLead = (leadId: string, opts: { fresh?: boolean; renewFrom?: string } = {}) => apiPost<{ contract: Contract; created: boolean }>(`/crm/contracts/by-lead/${leadId}`, opts)
export const ctListByLead = (leadId: string) => apiGet<{ contracts: CtSummary[] }>(`/crm/contracts/by-lead/${leadId}`).then((r) => r.contracts)
export const ctMonitoring = () => apiGet<{ upcomingPayments: CtMonitorCard[]; upcomingRenewals: CtMonitorCard[]; recentPayments: CtMonitorCard[]; failed: CtMonitorCard[]; contracts: number }>('/crm/contracts/monitoring')
export const ctMarkPayment = (contractId: string, dueAt: string, status: 'expected' | 'paid' | 'failed') => apiPost('/crm/contracts/payments/mark', { contractId, dueAt, status })
export const ctLegacyList = () => apiGet<{ legacy: CtLegacy[] }>('/crm/contracts/legacy').then((r) => r.legacy)
export const ctLegacySave = (body: Partial<CtLegacy> & { company: string; plan: string }) => apiPost<{ ok: boolean; id: string }>('/crm/contracts/legacy', body)
export const ctLegacyDelete = (id: string) => apiDelete(`/crm/contracts/legacy/${id}`)
export const ctLegacyConvert = (id: string, body: { plan: 'lite' | 'pro' | 'premium'; mrr?: number | null; frequency?: 'monthly' | 'annual' | null; commencedAt?: string | null }) => apiPost<{ ok: boolean; id: string }>(`/crm/contracts/legacy/${id}/convert`, body)
export const ctGet = (id: string) => apiGet<{ contract: Contract }>(`/crm/contracts/${id}`).then((r) => r.contract)
export const ctPatch = (id: string, body: { customer?: Record<string, any>; selections?: Partial<CtSelections>; options?: Partial<CtOptions>; proserv?: Record<string, any>; pricing?: CtPriceBook; sowFills?: Record<string, string> }) => patch<{ ok: boolean; contract: Contract }>(`/crm/contracts/${id}`, body)
export const ctLink = (id: string) => apiGet<{ url: string; slug: string }>(`/crm/contracts/${id}/link`)
export const ctRotate = (id: string) => apiPost<{ ok: boolean; url: string }>(`/crm/contracts/${id}/rotate-token`, {})
export const ctStatus = (id: string, status: 'draft' | 'sent' | 'void') => apiPost(`/crm/contracts/${id}/status`, { status })
export const ctCountersign = (id: string, name: string) => apiPost(`/crm/contracts/${id}/countersign`, { name })
export const ctSetStart = (id: string, startAt: string | null) => apiPost(`/crm/contracts/${id}/start-date`, { startAt })
export const ctPricebook = () => apiGet<{ pricebook: CtPriceBook }>('/crm/contracts/pricebook').then((r) => r.pricebook)
export const ctSavePricebook = (pricebook: CtPriceBook) => apiPut('/crm/contracts/pricebook', { pricebook })
export const getDealById = (id: string) => apiGet<any>(`/crm/deals/${id}`)

/* ─── WeQuote org users (Users/Contacts tab) ─────────────────────────────── */
export interface OrgUser {
  id: string
  name: string
  email: string | null
  phone: string | null
  title: string | null
  role: string | null
  isAdmin: boolean
  status: string
  lastLoginAt: string | null
}
export const getDealOrgUsers = (dealId: string) => apiGet<{ refreshed: boolean; users: OrgUser[] }>(`/crm/deals/${dealId}/org-users`)

/* ─── Conversation history ───────────────────────────────────────────────── */
export interface ConvoSummary {
  kind: 'support' | 'whatsapp'
  linked: boolean
  groupName: string | null
  summary: string | null
  itemCount: number
  fromAt: string | null
  toAt: string | null
  generatedAt: string | null
  cached: boolean
}
export const getConvoSummary = (dealId: string, kind: 'support' | 'whatsapp', regenerate = false) =>
  apiGet<ConvoSummary>(`/crm/deals/${dealId}/convo/${kind}${regenerate ? '?regenerate=1' : ''}`)
/** LIVE gateway groups — jid is the WhatsApp group id (…@g.us). */
export interface WaGroup { jid: string; name: string }
export const getWaGroups = (search?: string) =>
  apiGet<{ available: boolean; groups: WaGroup[] }>(`/crm/wa/groups${search ? `?search=${encodeURIComponent(search)}` : ''}`)
export const linkWaGroup = (dealId: string, jid: string | null, name?: string | null) =>
  apiPost<{ linked: boolean; groupName: string | null }>(`/crm/deals/${dealId}/wa-group`, { jid, name: name ?? null })
export interface FeatureRequest {
  id: string
  dealId: string
  title: string
  detail: string | null
  source: 'manual' | 'support' | 'whatsapp'
  status: 'open' | 'planned' | 'done' | 'rejected'
  createdBy: string | null
  createdAt: string | null
}
export const getFeatureRequests = (dealId: string) => apiGet<{ requests: FeatureRequest[] }>(`/crm/deals/${dealId}/feature-requests`).then((r) => r.requests)
export const createFeatureRequest = (dealId: string, body: { title: string; detail?: string | null }) => apiPost<FeatureRequest>(`/crm/deals/${dealId}/feature-requests`, body)
export const updateFeatureRequest = (id: string, body: { status?: string; title?: string; detail?: string | null }) => patch<FeatureRequest>(`/crm/feature-requests/${id}`, body)

// ── lead qualification (PRD v1.0 — server-computed gate; UI renders, never derives) ──
export type QualificationStatus = 'not_qualified' | 'working' | 'qualified' | 'disqualified' | 'nurture'
export interface QualOption { value: string; label: string }
export interface QualGate { pass: boolean; failed: boolean; missing: string[] }
export interface Qualification {
  dealId: string
  status: QualificationStatus
  gates: { icp: QualGate; need: QualGate; authority: QualGate; budget: QualGate; timeline: QualGate; engagement: QualGate }
  hold: 'nurture' | 'disqualified' | null
  /** Fi's PRD (18 Sep 2026): live 0–100 gate score, auto-fill provenance, unqualified-demo flag. */
  qualPct?: number
  autofill?: Record<string, { v: any; src: string; at: string }>
  demoFlag?: { at: string; pct: number | null; reason: string | null } | null
  fields: {
    icpVertical: string | null
    companySize: number | null
    geography: string | null
    useCaseFit: boolean | null
    needConfirmed: boolean | null
    needDetail: string | null
    authorityStatus: string | null
    budgetStatus: string | null
    monthlyQuoteVolume: number | null
    decisionTimeline: string | null
    engagedTwoWay: boolean | null
    discoveryCallDone: boolean | null
    /** = deals.amount — edited on Lead Details, shown here against the £1,800 gate. */
    estDealValue: number
  }
  leadScore: number | null
  leadGrade: string | null
  disqualifyReason: string | null
  qualifiedAt: string | null
  qualifiedBy: string | null
  options: {
    verticals: QualOption[]; geographies: QualOption[]; authority: QualOption[]
    budget: QualOption[]; timeline: QualOption[]; disqualifyReasons: QualOption[]
    thresholds: { minDeal: number; minQuoteVolume: number; minCompanySize: number }
  }
}
// ── Needs Your Attention dock (22 Sep 2026) ──
export interface AttentionItem {
  key: string; kind: 'alert' | 'risk' | 'trial' | 'call' | 'done_notice'; dealId: string | null; dealName: string
  title: string; detail: string; severity: 'info' | 'warning' | 'critical'
  at: string; done: boolean; doneAt?: string | null
  status: string | null; note: string | null
}
export const ATTENTION_STATUSES: { value: string; label: string }[] = [
  { value: 'contacted', label: 'Contacted' },
  { value: 'call_booked', label: 'Booked a call' },
  { value: 'email_sent', label: 'Email sent' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'resolved', label: 'Resolved' },
]
export const getAttention = (force = false) => apiGet<{ items: AttentionItem[]; open: number; completed: number }>(`/crm/attention${force ? '?force=1' : ''}`)
export const setAttentionDone = (key: string, done: boolean) => apiPost('/crm/attention/done', { key, done })
export const setAttentionStatus = (key: string, body: { status?: string | null; note?: string | null }) => apiPost('/crm/attention/status', { key, ...body })

// Quote-activity risk (Lee's one-rule at-risk, 22 Sep 2026) + push cadence
export interface QuoteRiskRow {
  dealId: string; name: string; stage: string; mrr: number; ownerId: string | null
  quotes14: number; lastQuoteAt: string | null; daysSinceQuote: number | null
  latestPeriodEnd: string | null; atRisk: boolean; dataStale: boolean; noData: boolean
}
export interface UsagePushCadence { pushHour: string; weekday: string; timeLondon: string; accounts: number; latestPeriodEnd: string | null }
export const getQuoteRisk = () => apiGet<{ rows: QuoteRiskRow[]; cadence: UsagePushCadence[] }>('/crm/quote-risk')

export const markLeadContacted = (dealId: string, note?: string) => apiPost(`/crm/deals/${dealId}/mark-contacted`, note ? { note } : {})
export const getQualification = (dealId: string) => apiGet<Qualification>(`/crm/deals/${dealId}/qualification`)
export const patchQualification = (
  dealId: string,
  body: { fields?: Record<string, any>; action?: 'nurture' | 'disqualify' | 'reopen' | 'qualify'; reason?: string }
) => patch<Qualification>(`/crm/deals/${dealId}/qualification`, body)

/* ─── Call queue (PRD-04) ────────────────────────────────────────────────── */
export interface QueueItem {
  id: string
  dealId: string
  dealName: string
  stage: string | null
  country: string | null
  mrr: number | null
  contactName: string | null
  phone: string | null
  phoneMissing: boolean
  email: string | null
  trigger: 'payment_failed' | 'card_removed' | 'trial_expired' | 'auto_trial' | 'imported' | 'unqualified'
  priority: number
  status: 'pending' | 'in_progress' | 'snoozed' | 'done' | 'removed'
  /** Show/event the lead was captured at (imports) — the queue's By-Event view. */
  event: string | null
  /** Live qualification badge for the entry's lead. */
  qualificationStatus: string
  assignedTo: string | null
  attempts: number
  note: string | null
  callbackAt: string | null
  daysInQueue: number
  enqueuedAt: string
}
export const getCallQueue = (f: { trigger?: string; assignee?: string; status?: string } = {}) => {
  const qs = Object.entries(f).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')
  return apiGet<{ items: QueueItem[]; counts: Record<string, number>; total: number }>(`/crm/call-queue${qs ? '?' + qs : ''}`)
}
export const queueAction = (id: string, body: { action: 'claim' | 'release' | 'outcome'; outcome?: string; note?: string | null; callbackAt?: string | null }) =>
  patch<QueueItem>(`/crm/call-queue/${id}`, body)
/**
 * Fetch the WHOLE deal list across pages. The API caps one request at limit=1000 and REJECTS
 * anything larger (limit: 2000 → 4xx → useCrm fell back to the now-empty fixture, silently
 * blanking five pages). Every page that needs "all deals" must use this.
 */
export const fetchAllDeals = async (params: Record<string, any> = {}): Promise<any[]> => {
  const size = 500
  const all: any[] = []
  for (let offset = 0; offset < 100000; offset += size) {
    const r = await getDeals({ ...params, limit: size, offset })
    const batch = r.deals || []
    all.push(...batch)
    if (batch.length < size) break
  }
  return all
}
export const getDeal = (id: string) => apiGet(`/crm/deals/${id}`)
export const createDeal = (body: any) => apiPost('/crm/deals', body)
export const updateDeal = (id: string, body: any) => patch(`/crm/deals/${id}`, body)
export const dismissCancelRequest = (id: string) => apiPost(`/crm/deals/${id}/cancel-request/dismiss`, {})
export const moveDealStage = (id: string, stage: string, lostReason?: string) => apiPost(`/crm/deals/${id}/stage`, { stage, lostReason })
/** Re-fire the signup WhatsApp alert (number + New Signups group) for a trial lead. */
export const resendTrialAlert = (id: string) => apiPost<{ ok: boolean; dm: number; group: string | null }>(`/crm/deals/${id}/resend-trial-alert`, {})
export const mergeDeals = (primaryId: string, secondaryId: string) => apiPost('/crm/deals/merge', { primaryId, secondaryId })
export const addActivity = (id: string, body: { type: string; body: string }) => apiPost(`/crm/deals/${id}/activities`, body)
export const addTask = (id: string, body: { title: string; dueInDays?: number }) => apiPost(`/crm/deals/${id}/tasks`, body)
export const updateTask = (id: string, body: { done?: boolean; title?: string; isNext?: boolean; dueAt?: string | null; ownerName?: string | null }) => patch(`/crm/tasks/${id}`, body)
export const reorderTasks = (dealId: string, ids: string[]) => apiPost(`/crm/deals/${dealId}/tasks/reorder`, { ids })
export const addQuote = (id: string, body: any) => apiPost(`/crm/deals/${id}/quotes`, body)

// Archive (soft-delete) — admin-only server-side. "Delete" in the CRM hides a row from every
// list/board/metric but keeps it recoverable. getDeals({ archived: true }) lists the archived.
export const archiveDeal = (id: string) => apiPost<{ id: string; archived: boolean }>(`/crm/deals/${id}/archive`, {})
export const restoreDeal = (id: string) => apiPost<{ id: string; archived: boolean }>(`/crm/deals/${id}/restore`, {})
export const archiveDeals = (ids: string[]) => apiPost<{ archived: number }>('/crm/deals/archive', { ids })

// companies / contacts / products
export const getCompanies = () => apiGet<{ companies: any[] }>('/crm/companies').then((r) => r.companies)
export const createCompany = (body: any) => apiPost('/crm/companies', body)
export const updateCompany = (id: string, body: any) => patch(`/crm/companies/${id}`, body)
export const getContacts = () => apiGet<{ contacts: any[] }>('/crm/contacts').then((r) => r.contacts)
export const createContact = (body: any) => apiPost('/crm/contacts', body)
export const updateContact = (id: string, body: any) => patch(`/crm/contacts/${id}`, body)
export const getProducts = () => apiGet<{ products: any[] }>('/crm/products').then((r) => r.products)

// opportunities
export const getOppGroups = () => apiGet<{ groups: any[] }>('/crm/opp-groups').then((r) => r.groups)
export const getOppGroup = (id: string) => apiGet(`/crm/opp-groups/${id}`)
export const createOppGroup = (body: any) => apiPost('/crm/opp-groups', body)
export const createOpp = (body: any) => apiPost('/crm/opps', body)
export const updateOpp = (id: string, body: any) => patch(`/crm/opps/${id}`, body)
export const convertOpp = (id: string) => apiPost(`/crm/opps/${id}/convert`, {})
// Archive shows/events, single captured contacts, contacts and companies (admin-only).
// archiveOppGroup: withLeads decides the fate of the leads the show produced (ask-each-time).
export const reorderShowLeads = (groupId: string, oppIds: string[]) => apiPost(`/crm/opp-groups/${groupId}/reorder`, { oppIds })
export const archiveOppGroup = (id: string, withLeads: boolean) => apiPost<{ id: string; archived: boolean; opps: number; leadsArchived: number }>(`/crm/opp-groups/${id}/archive`, { withLeads })
export const restoreOppGroup = (id: string) => apiPost(`/crm/opp-groups/${id}/restore`, {})
export const archiveOpp = (id: string) => apiPost(`/crm/opps/${id}/archive`, {})
export const restoreOpp = (id: string) => apiPost(`/crm/opps/${id}/restore`, {})
export const archiveContact = (id: string, force = false) => apiPost(`/crm/contacts/${id}/archive${force ? '?force=1' : ''}`, {})
export const archiveCompany = (id: string, force = false) => apiPost(`/crm/companies/${id}/archive${force ? '?force=1' : ''}`, {})

// CSV/Excel contact import. dryRun → { mapping, sample, willImport, ... } (no
// writes). Commit → { created, duplicates, dealIds }. Shared by Opportunities
// (with groupId) and Leads (no group). Multipart, so we bypass the JSON helpers.
export interface ImportPreview { dryRun: true; headers: string[]; mapping: Record<string, string>; mapped: number; totalRows: number; willImport: number; skipped: number; sample: { name: string; email: string; company: string; phone: string; title: string }[] }
export interface ImportResult { dryRun: false; created: number; duplicates: number; dealIds: string[]; source: string; totalRows: number; blankSkipped: number }
export const importContactsFile = async (file: File, opts: { groupId?: string; dryRun?: boolean } = {}): Promise<ImportPreview | ImportResult> => {
  const fd = new FormData()
  fd.append('file', file)
  if (opts.groupId) fd.append('groupId', opts.groupId)
  if (opts.dryRun) fd.append('dryRun', 'true')
  const token = localStorage.getItem('auth_token')
  const res = await fetch('/api/crm/import', { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd })
  if (!res.ok) {
    let detail: any = null
    try { detail = await res.json() } catch { /* ignore */ }
    const e: Error & { status?: number; detail?: any } = new Error(detail?.error?.message || detail?.message || 'Import failed')
    e.status = res.status; e.detail = detail
    throw e
  }
  return res.json()
}

// sequences / automations
export const getSequences = () => apiGet<{ sequences: any[] }>('/crm/sequences').then((r) => r.sequences)
export const toggleSequence = (id: string) => apiPost(`/crm/sequences/${id}/toggle`, {})
// email templates — block-based sections + {Tag} placeholders (ported from the
// WeQuote portal's notification_template model).
export interface EmailBlock {
  id: string
  type: 'header' | 'eyebrow' | 'heading' | 'text' | 'meta' | 'button' | 'image' | 'table' | 'divider' | 'spacer' | 'footer'
  text?: string; brand?: string; accent?: string; label?: string; url?: string; color?: string
  src?: string; alt?: string; height?: number
  rows?: { k: string; v: string }[]
  headers?: string[]; cells?: string[][]
  copyright?: string; companyInfo?: string; address?: string; contactEmail?: string
}
export interface TagGroup { key: string; label: string; tags: string[] }
export interface EmailTemplate { id: string; name: string; purpose: string; subject: string; body: string; bodyBlocks?: EmailBlock[]; updatedAt?: string }
export const getEmailTemplates = () =>
  apiGet<{ templates: EmailTemplate[]; purposes: string[]; tagGroups: TagGroup[]; blockTypes: { type: string; label: string }[] }>('/crm/email-templates')
export const previewEmailTemplate = (body: { subject?: string; body?: string; bodyBlocks?: EmailBlock[]; dealId?: string }) =>
  apiPost<{ subject: string; html: string; text: string; to: string; from: string }>('/crm/email-templates/preview', body)
export const createEmailTemplate = (body: Partial<EmailTemplate>) => apiPost<EmailTemplate>('/crm/email-templates', body)
export const updateEmailTemplate = (id: string, body: Partial<EmailTemplate>) => patch<EmailTemplate>(`/crm/email-templates/${id}`, body)
export const deleteEmailTemplate = (id: string) => apiDelete(`/crm/email-templates/${id}`)

// Duplicate hints (Phase 2.10) — confidence is earned, not assumed.
export type Confidence = 'certain' | 'likely' | 'possible'
export interface DuplicateCluster {
  confidence: Confidence
  evidence: string
  leads: { dealId: string; company: string; stage: string; email: string | null; createdDays: number; owner: string | null }[]
}
export interface DuplicateReport { clusters: DuplicateCluster[]; counts: { certain: number; likely: number; possible: number }; note: string }
export const getDuplicates = () => apiGet<DuplicateReport>('/crm/duplicates')
export const getDealDuplicates = (dealId: string) => apiGet<{ duplicate: boolean; confidence?: Confidence; evidence?: string; others?: { dealId: string; company: string; stage: string }[] }>(`/crm/deals/${dealId}/duplicates`)

// Owner identity (Phase 2.9) — an owner_id that is not a real agent is an owner of nobody.
export interface OwnerAudit {
  total: number; resolvable: number; unresolvable: number; unowned: number
  orphans: { ownerId: string; leads: number; leadOwnerText: string | null; suggestion: { agentId: string; name: string; why: string } | null }[]
  agents: { id: string; name: string; email: string }[]
  note: string
}
export const getOwnerAudit = () => apiGet<OwnerAudit>('/crm/owners/audit')
export const reconcileOwners = (mapping: Record<string, string>, dryRun = true) =>
  apiPost<{ dryRun: boolean; results: { from: string; to: string; leads: number; applied: boolean; error?: string }[]; totalLeads: number }>('/crm/owners/reconcile', { mapping, apply: !dryRun })

// Report builder that can see what Set 2 built (Phase 2.8). Every row carries its sample size.
export interface ReportRow { label: string; value: number; n: number; of: number; provisional: boolean }
export interface ReportResult {
  dimension: string; metric: string; metricLabel: string
  rows: ReportRow[]
  coverage: { withEvidence: number; total: number; note: string }
}
export const getReportSchema = () => apiGet<{ dimensions: { key: string; label: string }[]; metrics: { key: string; label: string; needsEvidence: boolean }[] }>('/crm/reports/schema')
export const runReport = (dimension: string, metric: string) => apiPost<ReportResult>('/crm/reports/run', { dimension, metric })

// Funnel velocity from the stage HISTORY (Phase 2.7) — not a snapshot of where deals sit.
export interface StageVelocity {
  key: string; label: string
  entered: number; movedOn: number; lost: number; current: number
  passRate: number | null; medianDays: number | null
  medianSample: number; medianProvisional: boolean; medianReconstructed: boolean
  /** The REAL number stuck. `stuck` below is only the worst few, for display. */
  stuckCount: number
  stuck: { dealId: string; company: string; days: number; owner: string | null }[]
  stuckThresholdDays: number | null
}
export interface Velocity {
  stages: StageVelocity[]
  medianDaysToCustomer: number | null
  medianDaysToCustomerSample: number
  totalStuck: number
  coverage: { dealsWithHistory: number; dealsTotal: number; reconstructedMoves: number; recordedMoves: number; note: string }
}
export const getVelocity = () => apiGet<Velocity>('/crm/velocity')

// Alerts that actually reach someone (Phase 2.6).
export interface CrmAlert { id: string; dealId: string | null; dealName: string; title: string; body: string | null; severity: 'info' | 'warn' | 'critical'; source: string; read: boolean; createdAt: string }
export const getAlerts = () => apiGet<{ unread: number; alerts: CrmAlert[] }>('/crm/alerts')
export const markAlertRead = (id: string) => apiPost(`/crm/alerts/${id}/read`, {})
export const markAllAlertsRead = () => apiPost('/crm/alerts/read-all', {})
export const getAlertStatus = () => apiGet<{ pushTokensRegistered: number; dealsWithAnOwner: number; dealsTotal: number; mobilePush: boolean; note: string }>('/crm/alerts/status')

// Sequence triggers + enrolment preview (Phase 2.5). Nothing ever enrolled a lead into a
// sequence before this, so no sequence had ever run.
export interface SequenceTrigger { value: string; label: string; description: string; group: string }
export interface SequencePreview {
  sequenceId: string; name: string; trigger: string | null; active: boolean
  matches: number; wouldEnrol: number; alreadyEnrolled: number
  sample: { dealId: string; company: string }[]
  sendsRealEmail: boolean
}
export const getSequenceTriggers = () => apiGet<{ triggers: SequenceTrigger[] }>('/crm/sequences/triggers').then((r) => r.triggers)
export const previewSequence = (id: string) => apiGet<SequencePreview>(`/crm/sequences/${id}/preview`)
export const getTriggerMatchCount = (trigger: string, days?: string) =>
  apiGet<{ trigger: string; matches: number }>(`/crm/sequences/trigger-match?trigger=${encodeURIComponent(trigger)}${days ? `&days=${days}` : ''}`)

export const createSequence = (body: any) => apiPost('/crm/sequences', body)
export const updateSequence = (id: string, body: any) => patch(`/crm/sequences/${id}`, body)
export const deleteSequence = (id: string) => apiDelete(`/crm/sequences/${id}`)
export const getAutomations = () => apiGet<{ automations: any[] }>('/crm/automations').then((r) => r.automations)
export const toggleAutomation = (id: string) => apiPost(`/crm/automations/${id}/toggle`, {})
export const createAutomation = (body: any) => apiPost('/crm/automations', body)
export const updateAutomation = (id: string, body: any) => patch(`/crm/automations/${id}`, body)
export const deleteAutomation = (id: string) => apiDelete(`/crm/automations/${id}`)

// flags (shared marker layer)
export const getFlags = () => apiGet<{ flags: any[] }>('/crm/flags').then((r) => r.flags)
export const createFlag = (body: { name: string; color?: string }) => apiPost('/crm/flags', body)
export const deleteFlag = (id: string) => apiDelete(`/crm/flags/${id}`)
export const getDealFlags = (dealId: string) => apiGet<{ flags: any[] }>(`/crm/deals/${dealId}/flags`).then((r) => r.flags)
export const addDealFlag = (dealId: string, flagId: string) => apiPost(`/crm/deals/${dealId}/flags`, { flagId })
export const removeDealFlag = (dealId: string, flagId: string) => apiDelete(`/crm/deals/${dealId}/flags/${flagId}`)

// analytics / settings / ai
export const getAnalytics = () => apiGet('/crm/analytics')
export const getDemoFunnel = () => apiGet('/crm/demo-funnel')
export const getSettings = () => apiGet('/crm/settings')
export const putSettings = (body: any) => apiPut('/crm/settings', body)
export const aiDealHealth = (deal: any) => apiPost('/crm/ai/deal-health', { deal })
export const aiChurn = (account: any) => apiPost('/crm/ai/churn', { account })
export const aiSummarise = (notes: string) => apiPost('/crm/ai/summarise', { notes })

/* ─── Pipeline reporting (PRD 3) ─────────────────────────────────────────── */
export interface StageProb { stage: string; calculated: number | null; sampleSize: number; nextRate: number | null; nextSample: number; lowConfidence: boolean; override: number | null; overrideBy: string | null; overrideAt: string | null; overrideReason: string | null; provisional: boolean; applied: number; computedAt: string | null }
export interface PipelineStageRow { stage: string; band?: string | null; trialActiveGroup?: boolean; endingSoon?: number; count: number; gross: number; weighted: number; weightedCalc: number; estimatedShare: number; probs: StageProb | null }
export interface PipelineBands { a: { count: number; gross: number; weighted: number; weightedCalc: number }; b1: { count: number; gross: number; weighted: number; weightedCalc: number }; b2: { count: number; gross: number; weighted: number; weightedCalc: number; recoverableArr: number }; closedWon: { count: number; arr: number }; saveAlerts: number }
export interface PipelineReport { stages: PipelineStageRow[]; bands?: PipelineBands; totals: { count: number; gross: number; weighted: number; weightedCalc: number; overrideDelta: number }; forecast: { likely: number; commit: number; bestCase: number }; owners: { ownerId: string | null; count: number; gross: number; weighted: number; weightedCalc: number; commit: number }[]; deals: { id: string; name: string; stage: string; ownerId: string | null; source: string | null; value: number; valueEstimated: boolean; applied: number; calculated: number; overridden: boolean; weighted: number; weightedCalc: number }[]; generatedAt: string }
export interface ProbAuditRow { id: string; scope: string; refId: string; refLabel: string | null; oldValue: number | null; newValue: number | null; who: string | null; reason: string | null; at: string }
export const getPipelineReport = (f: { owner?: string; source?: string; country?: string; days?: number } = {}) => {
  const qs = Object.entries(f).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')
  return apiGet<PipelineReport>(`/crm/pipeline/report${qs ? '?' + qs : ''}`)
}
export const setStageOverride = (stage: string, body: { override: number | null; reason?: string; provisional?: boolean }) => apiPut(`/crm/pipeline/stages/${stage}`, body)
export const setDealProbability = (dealId: string, body: { override: number | null; reason?: string }) => apiPut(`/crm/deals/${dealId}/probability`, body)
export const getProbAudit = (scope?: string) => apiGet<{ audit: ProbAuditRow[] }>(`/crm/pipeline/audit${scope ? '?scope=' + scope : ''}`).then((r) => r.audit)
export const refreshPipeline = () => apiPost('/crm/pipeline/refresh', {})

export const takePipelineSnapshot = () => apiPost('/crm/pipeline/snapshots', {})
export const getPipelineSnapshots = () => apiGet<{ snapshots: any[] }>('/crm/pipeline/snapshots').then((r) => r.snapshots)
export const getPipelineAccuracy = () => apiGet<any>('/crm/pipeline/accuracy')

/* ─── Bookings — scheduled calls / meetings (PRD-04) ─────────────────────── */
export interface Booking {
  id: string
  dealId: string
  type: 'call' | 'meeting'
  title: string
  startAt: string | null
  endAt: string | null
  status: 'planned' | 'done' | 'cancelled'
  createdBy: string | null
  recordingUrl: string | null
  transcriptUrl: string | null
  assigneeId: string | null
  phone: string | null
  attendees: string[]
  note: string | null
  /** null until the Google Meet integration lands. */
  meetUrl: string | null
  /** The caller's logged remarks — REQUIRED to mark a call done. */
  logText: string | null
  logLinks: string[]
  loggedAt: string | null
  /** Mirrored Google Calendar event id (meetings) — de-dupe key for the Google feed. */
  googleEventId?: string | null
  doneAt: string | null
  overdue: boolean
  upcoming: boolean
  /** Gemini notes exist for this meeting (content is PRIVATE to notesOwner). */
  hasNotes?: boolean
  notesOwner?: string | null
  /** Held vs no-show — auto from Meet participant records, manually editable. */
  attendanceStatus?: 'held' | 'no_show' | null
  attendanceSource?: 'auto' | 'manual' | null
  /** Present on agenda rows only. */
  dealName?: string | null
}
/** Private notes read — content only when the viewer is the recorder. */
export interface BookingNotes {
  bookingId: string
  hasNotes: boolean
  owner: string | null
  visible: boolean
  notesText: string | null
  docUrl: string | null
  pulledAt: string | null
  /** One entry per recorder (Lee, 9 Oct 2026) — visible to everyone. */
  notes?: Array<{ owner: string | null; docUrl: string | null; notesText: string | null; pulledAt: string | null }>
}
export const getBookingNotes = (id: string) => apiGet<BookingNotes>(`/crm/bookings/${id}/notes`)
// (19 Aug overnight pass: the v3 intake/setup client fns — getTaskDashboard,
// beginSetup, getPipelineState, completeSetupStep, completeIntakeTask — were
// superseded by the journey below and had no remaining web references. The
// SERVER endpoints all still exist; mobile keeps using them.)
export const getLeadSummary = (dealId: string) => apiGet<{ dealId: string; summary: string }>(`/crm/deals/${dealId}/summary`)

// ── Task Dashboard v4: the three-column JOURNEY (Lee, 19 Aug 2026) ───────────
export type JourneyStageKey = 'pre_demo' | 'demo_setup' | 'post_trial'
export interface JourneyItem { key: string; label: string; gate: boolean; done: boolean; at: string | null; by?: string | null }
export interface JourneyCard {
  dealId: string
  dealName: string
  ownerId: string | null
  stage: string
  signupAt: string
  contactedAt: string | null
  note: string | null
  items: JourneyItem[]
}
export interface JourneyDashboard {
  columns: Record<JourneyStageKey, JourneyCard[]>
  counts: Record<JourneyStageKey, number>
  generatedAt: string
}
export const getJourneyDashboard = () => apiGet<JourneyDashboard>('/crm/journey-dashboard')

// ── CRM Gmail sync: lead mini-inbox + mailbox management (Lee, 19 Aug 2026) ──
export interface LeadEmail {
  id: number
  threadId: string
  account: string
  direction: 'in' | 'out'
  from: string
  to: string[]
  cc: string[]
  subject: string | null
  sentAt: string
  snippet: string | null
  body: string | null
  matchRule: string | null
  attachments: { filename: string; mimeType: string; sizeBytes: number }[]
}
export interface EmailAccount {
  googleEmail: string
  agentEmail: string
  status: string
  connectedAt: string
  lastSyncAt: string | null
  backfilled: boolean
  watchActive: boolean
  lastError: string | null
}
export interface EmailReviewItem {
  id: number
  reason: string
  candidates: { dealId: string; name: string; score: number }[]
  from: string
  subject: string | null
  snippet: string | null
  sentAt: string
  direction: string
  account: string
}
/** Deal-level call outcome (399) — works with OR without an open queue entry. */
export const logCallOutcome = (dealId: string, body: { outcome: string; note?: string | null; callbackAt?: string | null }) =>
  apiPost<{ ok: boolean; via: string }>(`/crm/deals/${dealId}/call-outcome`, body)
export const getDealEmails = (dealId: string) => apiGet<{ emails: LeadEmail[] }>(`/crm/deals/${dealId}/emails`).then((r) => r.emails)
export const getEmailAccounts = () => apiGet<{ accounts: EmailAccount[] }>('/crm/email-accounts').then((r) => r.accounts)
export const startGmailConnect = () => apiGet<{ url: string }>('/crm/email/oauth/start')
export const disconnectGmail = (googleEmail: string) => apiPost('/crm/email-accounts/disconnect', { googleEmail })
export const getEmailReview = () => apiGet<{ items: EmailReviewItem[] }>('/crm/email-review').then((r) => r.items)
export const resolveEmailReview = (id: number, dealId: string | null) => apiPost(`/crm/email-review/${id}/resolve`, { dealId })
export const getDealJourney = (dealId: string) => apiGet<{ dealId: string; stage: string; note: string | null; items: JourneyItem[] }>(`/crm/deals/${dealId}/journey`)
export const tickJourneyItem = (dealId: string, key: string, undo = false) => apiPost<{ ok: boolean; stage: string }>(`/crm/deals/${dealId}/journey/${key}`, { undo })
export const setJourneyNote = (dealId: string, note: string) => apiPost(`/crm/deals/${dealId}/journey-note`, { note })

// ── funnel Gates 6–8: adoption levels ────────────────────────────────────────
export interface AdoptionReport {
  generatedAt: string
  payingCustomers: number
  distribution: Record<string, number>
  churnRisk: { dealId: string; name: string; mrr: number; level: number; signals: string[] }[]
  mrrAtRisk: number
  gateBreaches: { g6: number; g7: number; g8a: number; g8b: number; g8c: number }
  dataBasis: string
}
export const getAdoptionReport = () => apiGet<AdoptionReport>('/crm/funnel/adoption')

// ── funnel Gates 2–5: gate report + blockers ─────────────────────────────────
export interface GateReport {
  generatedAt: string
  dealsInFunnel: number
  throughAllGates: number
  noShowsTotal: number
  gates: { gate: number; label: string; atGate: number; breached: number; longestWaiting: { dealId: string; name: string; days: number } | null }[]
  blockers: { value: string; label: string }[]
}
export const getGateFunnel = (owner?: string) => apiGet<GateReport>(`/crm/funnel/gates${owner ? `?owner=${encodeURIComponent(owner)}` : ''}`)
export const setDealBlocker = (dealId: string, blocker: string, note: string | null) => {
  return apiFetch(`/crm/deals/${dealId}/blocker`, { method: 'PATCH', body: JSON.stringify({ blocker, note }) }).then((r) => r.json())
}

// ── funnel Gate 1: priority lane + speed-to-contact ──────────────────────────
export interface LaneItem {
  dealId: string
  name: string
  country: string | null
  signedUpAt: string
  clockStartAt: string
  state: 'green' | 'amber' | 'red' | 'critical' | 'waiting'
  elapsedMins: number
  attempted: boolean
  contactName: string | null
  phone: string | null
  ownerId: string | null
  queueItemId: string | null
}
export interface PriorityLane {
  items: LaneItem[]
  counts: { total: number; red: number; critical: number }
  coverHours: string
}
export const getPriorityLane = () => apiGet<PriorityLane>('/crm/priority-lane')
export interface SpeedToContact {
  periodDays: number
  signups: number
  contacted: number
  uncontacted: number
  medianMins: number | null
  meanMins: number | null
  within5: number | null
  within15: number | null
  within60: number | null
  within1440: number | null
  slowest: { name: string; mins: number }[]
}
export const getSpeedToContact = (days = 7) => apiGet<SpeedToContact>(`/crm/speed-to-contact?days=${days}`)

/** Manually set/clear Held vs No-Show — a manual value is never auto-overwritten. */
export const setBookingAttendance = (id: string, status: 'held' | 'no_show' | null) => {
  return apiFetch(`/crm/bookings/${id}/attendance`, { method: 'PATCH', body: JSON.stringify({ status }) }).then((r) => r.json())
}
export interface BookingPrefill {
  dealName: string
  country: string | null
  /** Server-resolved IANA timezone for the lead (state → area code → dial code → country). */
  tz?: string
  phone: string | null
  phones: string[]
  email: string | null
  emails: string[]
  /** Named lead contacts for the attendee type-ahead. */
  contacts: { name: string | null; email: string }[]
}
export interface BookingInput {
  type: 'call' | 'meeting'
  title?: string
  startAt: string
  endAt?: string | null
  assigneeId?: string | null
  phone?: string | null
  attendees?: string[]
  note?: string | null
  /** One-line justification when booking a demo under the qualification threshold (server 409s without it). */
  qualReason?: string | null
}
export const getDealBookings = (dealId: string) => apiGet<{ bookings: Booking[] }>(`/crm/deals/${dealId}/bookings`).then((r) => r.bookings)
export const getBookingPrefill = (dealId: string) => apiGet<BookingPrefill>(`/crm/deals/${dealId}/booking-prefill`)
export const getBookingAgenda = (assigneeId: string, day: string) =>
  apiGet<{ agenda: Booking[] }>(`/crm/bookings/agenda?assigneeId=${encodeURIComponent(assigneeId)}&day=${day}`).then((r) => r.agenda)
export const getNextBookingsByDeal = () => apiGet<{ byDeal: Record<string, Booking> }>('/crm/bookings/next-by-deal').then((r) => r.byDeal)
export interface GcalEvent {
  /** Google event id — precise de-dupe key against bookings' googleEventId. */
  gid?: string | null
  title: string
  startAt: string
  endAt: string | null
  allDay: boolean
  meetUrl: string | null
  /** External attendee emails (schedule feed only). */
  attendees?: string[]
  /** Lead correlation (Phase 1): matched via attendee email, or corporate domain. */
  dealId?: string | null
  dealName?: string | null
  matchedBy?: 'email' | 'domain' | null
  matchedEmail?: string | null
}
export interface DayDemo { id: string; title: string; dealName: string | null; startAt: string | null; endAt: string | null; meetUrl: string | null; inviteeName: string | null; hostEmail: string | null; mine?: boolean }
export const getDemosDay = (day: string) => apiGet<{ demos: DayDemo[] }>(`/crm/bookings/demos-day?day=${day}`).then((r) => r.demos)
export const getGcalDay = (email: string, day: string) =>
  apiGet<{ available: boolean; events: GcalEvent[]; reason?: string }>(`/crm/bookings/gcal?email=${encodeURIComponent(email)}&day=${day}`)
export const createBooking = (dealId: string, body: BookingInput) => apiPost<Booking>(`/crm/deals/${dealId}/bookings`, body)
export const updateBooking = (id: string, body: Partial<BookingInput> & { status?: 'planned' | 'done' | 'cancelled'; logText?: string | null; logLinks?: string[] }) =>
  patch<Booking>(`/crm/bookings/${id}`, body)
export interface ScheduleBooking extends Booking {
  dealName: string | null
  dealStage: string | null
  contactName: string | null
  contactPhones: string[]
  contactEmail: string | null
}
export interface ScheduleDemo {
  source: 'calendly'
  id: string
  dealId: string | null
  dealName: string | null
  title: string
  startAt: string | null
  endAt: string | null
  meetUrl: string | null
  inviteeName: string | null
  inviteeEmail: string | null
  hostEmail?: string | null
  /** This agent hosts it. */
  mine?: boolean
  /** No personal host captured (unassigned / shared mailbox). */
  hostShared?: boolean
}
/** The agent's WHOLE diary — CRM bookings + Google Calendar + hosted Calendly demos. */
export interface ScheduleFeed {
  bookings: ScheduleBooking[]
  google: { available: boolean; events: GcalEvent[] }
  demos: ScheduleDemo[]
}
export const getMySchedule = (from?: string, to?: string) => {
  const qs = [from && `from=${from}`, to && `to=${to}`].filter(Boolean).join('&')
  return apiGet<ScheduleFeed>(`/crm/bookings/mine${qs ? '?' + qs : ''}`)
}
